import React, { useState, useEffect } from 'react';
import { Plus, Minus, Trash2, CreditCard, Smartphone, Tag, Receipt, Wine, Sparkles, Search, User, Settings, ShoppingCart, X, Check, Printer, Clock, Save, RefreshCw, Bell, DollarSign, TrendingUp, Users } from 'lucide-react';

const ClubPOS = () => {
  const [cart, setCart] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [discount, setDiscount] = useState(0);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({ name: '', phone: '', email: '', table: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [savedOrders, setSavedOrders] = useState([]);
  const [showSavedOrders, setShowSavedOrders] = useState(false);
  const [orderNote, setOrderNote] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const products = [
    { id: 1, name: 'Dom Pérignon Vintage 2013', price: 189.99, category: 'champagne', image: '🥂', stock: 12, description: 'Legendary champagne with exceptional finesse' },
    { id: 2, name: 'Hennessy XO Cognac', price: 199.99, category: 'cognac', image: '🥃', stock: 8, description: 'Premium cognac with rich complexity' },
    { id: 3, name: 'Macallan 18 Single Malt', price: 449.99, category: 'whiskey', image: '🥃', stock: 5, description: 'Exceptional Scottish single malt whiskey' },
    { id: 4, name: 'Opus One 2018', price: 389.99, category: 'wine', image: '🍷', stock: 15, description: 'Bordeaux-style blend from Napa Valley' },
    { id: 5, name: 'Grey Goose Vodka', price: 49.99, category: 'vodka', image: '🍸', stock: 25, description: 'Ultra-premium French vodka' },
    { id: 6, name: 'Johnnie Walker Blue Label', price: 179.99, category: 'whiskey', image: '🥃', stock: 10, description: 'Rare and exceptionally smooth blend' },
    { id: 7, name: 'Moët & Chandon Rosé', price: 89.99, category: 'champagne', image: '🥂', stock: 20, description: 'Elegant rosé champagne' },
    { id: 8, name: 'Rémy Martin VSOP', price: 89.99, category: 'cognac', image: '🥃', stock: 18, description: 'Smooth and aromatic cognac' },
    { id: 9, name: 'Patron Silver Tequila', price: 79.99, category: 'tequila', image: '🍹', stock: 22, description: 'Premium 100% agave tequila' },
    { id: 10, name: 'Bombay Sapphire Gin', price: 39.99, category: 'gin', image: '🍸', stock: 30, description: 'Classic London dry gin' },
    { id: 11, name: 'Club Mixer Package', price: 15.99, category: 'mixers', image: '🥤', stock: 50, description: 'Soda, juice, and mixer selection' },
    { id: 12, name: 'Premium Ice Bucket', price: 25.99, category: 'accessories', image: '🧊', stock: 15, description: 'Chilled service essentials' },
  ];

  const categories = [
    { id: 'all', name: 'All Products', icon: '🍾' },
    { id: 'wine', name: 'Wines', icon: '🍷' },
    { id: 'champagne', name: 'Champagne', icon: '🥂' },
    { id: 'whiskey', name: 'Whiskey', icon: '🥃' },
    { id: 'cognac', name: 'Cognac', icon: '🥃' },
    { id: 'vodka', name: 'Vodka', icon: '🍸' },
    { id: 'tequila', name: 'Tequila', icon: '🍹' },
    { id: 'gin', name: 'Gin', icon: '🍸' },
    { id: 'mixers', name: 'Mixers', icon: '🥤' },
    { id: 'accessories', name: 'Accessories', icon: '🧊' },
  ];

  const coupons = {
    'CLUB10': { discount: 10, type: 'percentage', description: '10% club member discount' },
    'HAPPY25': { discount: 25, type: 'fixed', description: '$25 off happy hour' },
    'VIP15': { discount: 15, type: 'percentage', description: '15% VIP member discount' },
    'BIRTHDAY20': { discount: 20, type: 'percentage', description: '20% birthday special' },
  };

  const filteredProducts = products.filter(product => {
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
  };

  const updateQuantity = (id, change) => {
    setCart(prev => prev.map(item => {
      if (item.id === id) {
        const newQuantity = item.quantity + change;
        return newQuantity > 0 ? { ...item, quantity: newQuantity } : null;
      }
      return item;
    }).filter(Boolean));
  };

  const removeFromCart = (id) => {
    setCart(prev => prev.filter(item => item.id !== id));
  };

  const applyCoupon = () => {
    if (coupons[couponCode]) {
      setAppliedCoupon(coupons[couponCode]);
      setCouponCode('');
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
  };

  const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const couponDiscount = appliedCoupon ? 
    (appliedCoupon.type === 'percentage' ? subtotal * (appliedCoupon.discount / 100) : appliedCoupon.discount) : 0;
  const manualDiscount = subtotal * (discount / 100);
  const totalDiscount = couponDiscount + manualDiscount;
  const taxRate = 0.08;
  const taxAmount = (subtotal - totalDiscount) * taxRate;
  const total = subtotal - totalDiscount + taxAmount;

  const saveOrder = () => {
    if (cart.length === 0) return;
    
    const orderData = {
      id: Date.now(),
      items: [...cart],
      customer: { ...customerInfo },
      subtotal,
      discount: totalDiscount,
      tax: taxAmount,
      total,
      appliedCoupon,
      note: orderNote,
      timestamp: new Date().toISOString(),
      status: 'pending'
    };
    
    setSavedOrders(prev => [...prev, orderData]);
    setCart([]);
    setAppliedCoupon(null);
    setDiscount(0);
    setCustomerInfo({ name: '', phone: '', email: '', table: '' });
    setOrderNote('');
    alert('Order saved successfully! Receipt can be printed.');
  };

  const printReceipt = (order = null) => {
    const orderData = order || {
      items: cart,
      customer: customerInfo,
      subtotal,
      discount: totalDiscount,
      tax: taxAmount,
      total,
      appliedCoupon,
      note: orderNote,
      timestamp: new Date().toISOString()
    };
    
    // In a real implementation, this would send to printer
    console.log('Printing receipt:', orderData);
    alert('Receipt sent to printer!');
  };

  const loadSavedOrder = (order) => {
    setCart(order.items);
    setCustomerInfo(order.customer);
    setAppliedCoupon(order.appliedCoupon);
    setOrderNote(order.note);
    setShowSavedOrders(false);
  };

  const processPayment = async () => {
    setIsProcessing(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    setIsProcessing(false);
    setShowPayment(false);
    setCart([]);
    setAppliedCoupon(null);
    setDiscount(0);
    setCustomerInfo({ name: '', phone: '', email: '', table: '' });
    setOrderNote('');
    alert('Payment processed successfully!');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex h-screen">
        {/* Left Panel - Products */}
        <div className="flex-1 p-6 overflow-y-auto bg-white">
          {/* Header */}
          <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-gray-100">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl shadow-lg">
                <Wine className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-800">
                  Club <span className="text-purple-600">Vintique</span>
                </h1>
                <p className="text-gray-600 font-medium">Premium Club Experience</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm text-gray-500">Current Time</p>
                <p className="font-semibold text-gray-800">{currentTime.toLocaleTimeString()}</p>
              </div>
              <button 
                onClick={() => setShowSavedOrders(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors border border-blue-200"
              >
                <Clock className="h-5 w-5" />
                <span>Saved Orders</span>
                {savedOrders.length > 0 && (
                  <span className="bg-blue-600 text-white px-2 py-1 rounded-full text-xs">
                    {savedOrders.length}
                  </span>
                )}
              </button>
              <button className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                <User className="h-5 w-5 text-gray-600" />
              </button>
              <button className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                <Settings className="h-5 w-5 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative mb-6">
            <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search premium wines & spirits..."
              className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Categories */}
          <div className="flex space-x-2 mb-6 overflow-x-auto pb-2">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-xl whitespace-nowrap transition-all border ${
                  selectedCategory === category.id
                    ? 'bg-purple-600 text-white border-purple-600 shadow-lg'
                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                }`}
              >
                <span className="text-lg">{category.icon}</span>
                <span className="font-medium">{category.name}</span>
              </button>
            ))}
          </div>

          {/* Products Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredProducts.map((product) => (
              <div key={product.id} className="bg-white rounded-xl p-4 border border-gray-200 hover:border-purple-300 transition-all duration-200 hover:shadow-lg">
                <div className="text-center mb-4">
                  <div className="text-4xl mb-2">{product.image}</div>
                  <h3 className="text-lg font-semibold mb-1 text-gray-800">{product.name}</h3>
                  <p className="text-gray-600 text-sm mb-2">{product.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-purple-600">${product.price}</span>
                    <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      Stock: {product.stock}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => addToCart(product)}
                  disabled={product.stock === 0}
                  className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 disabled:from-gray-400 disabled:to-gray-400 text-white py-2 rounded-lg font-semibold transition-all duration-200 transform hover:scale-105 disabled:cursor-not-allowed"
                >
                  {product.stock === 0 ? 'Out of Stock' : 'Add to Cart'}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Right Panel - Cart */}
        <div className="w-80 bg-white border-l-2 border-gray-100 p-4 overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold flex items-center space-x-2 text-gray-800">
              <ShoppingCart className="h-6 w-6" />
              <span>Current Order</span>
            </h2>
            <span className="bg-purple-600 text-white px-2 py-1 rounded-full text-sm">
              {cart.reduce((sum, item) => sum + item.quantity, 0)}
            </span>
          </div>

          {/* Customer Info */}
          <div className="mb-4">
            <h3 className="font-semibold mb-2 flex items-center space-x-2 text-gray-800">
              <User className="h-4 w-4" />
              <span>Customer Info</span>
            </h3>
            <div className="space-y-2">
              <input
                type="text"
                placeholder="Customer Name"
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                value={customerInfo.name}
                onChange={(e) => setCustomerInfo(prev => ({ ...prev, name: e.target.value }))}
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text"
                  placeholder="Table #"
                  className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                  value={customerInfo.table}
                  onChange={(e) => setCustomerInfo(prev => ({ ...prev, table: e.target.value }))}
                />
                <input
                  type="tel"
                  placeholder="Phone"
                  className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                  value={customerInfo.phone}
                  onChange={(e) => setCustomerInfo(prev => ({ ...prev, phone: e.target.value }))}
                />
              </div>
            </div>
          </div>

          {/* Order Notes */}
          <div className="mb-4">
            <textarea
              placeholder="Order notes..."
              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm resize-none"
              rows="2"
              value={orderNote}
              onChange={(e) => setOrderNote(e.target.value)}
            />
          </div>

          {/* Cart Items */}
          <div className="space-y-2 mb-4 max-h-60 overflow-y-auto">
            {cart.length === 0 ? (
              <div className="text-center py-6 text-gray-500">
                <ShoppingCart className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Your cart is empty</p>
              </div>
            ) : (
              cart.map((item) => (
                <div key={item.id} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-sm text-gray-800">{item.name}</h4>
                    <button
                      onClick={() => removeFromCart(item.id)}
                      className="text-red-500 hover:text-red-700 p-1"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => updateQuantity(item.id, -1)}
                        className="bg-gray-200 hover:bg-gray-300 rounded-md p-1 text-gray-700"
                      >
                        <Minus className="h-3 w-3" />
                      </button>
                      <span className="w-8 text-center text-sm font-medium">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.id, 1)}
                        className="bg-gray-200 hover:bg-gray-300 rounded-md p-1 text-gray-700"
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">${item.price}</p>
                      <p className="font-bold text-purple-600">${(item.price * item.quantity).toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Coupon Section */}
          <div className="mb-4">
            <div className="flex items-center space-x-2 mb-2">
              <Tag className="h-4 w-4 text-purple-600" />
              <span className="font-medium text-gray-800">Discounts</span>
            </div>
            <div className="flex space-x-2 mb-2">
              <input
                type="text"
                placeholder="Coupon code"
                className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
              />
              <button
                onClick={applyCoupon}
                className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-sm"
              >
                Apply
              </button>
            </div>
            {appliedCoupon && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-2 mb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-700 font-medium text-sm">Coupon Applied!</p>
                    <p className="text-xs text-green-600">{appliedCoupon.description}</p>
                  </div>
                  <button
                    onClick={removeCoupon}
                    className="text-green-600 hover:text-green-700"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
            <div className="flex items-center space-x-2">
              <input
                type="range"
                min="0"
                max="20"
                value={discount}
                onChange={(e) => setDiscount(Number(e.target.value))}
                className="flex-1"
              />
              <span className="text-xs text-gray-500 w-16">{discount}% off</span>
            </div>
          </div>

          {/* Order Summary */}
          <div className="bg-gray-50 rounded-lg p-3 mb-4 border border-gray-200">
            <h3 className="font-semibold mb-2 text-gray-800">Order Summary</h3>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              {couponDiscount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Coupon Discount</span>
                  <span>-${couponDiscount.toFixed(2)}</span>
                </div>
              )}
              {manualDiscount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Manual Discount ({discount}%)</span>
                  <span>-${manualDiscount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-gray-600">
                <span>Tax (8%)</span>
                <span>${taxAmount.toFixed(2)}</span>
              </div>
              <div className="border-t border-gray-300 pt-2 mt-2">
                <div className="flex justify-between text-lg font-bold text-gray-800">
                  <span>Total</span>
                  <span>${total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-2">
            <button
              onClick={saveOrder}
              disabled={cart.length === 0}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white py-2 rounded-lg font-semibold transition-all duration-200 disabled:cursor-not-allowed flex items-center justify-center space-x-2 text-sm"
            >
              <Save className="h-4 w-4" />
              <span>Save & Print Receipt</span>
            </button>
            <button
              onClick={() => printReceipt()}
              disabled={cart.length === 0}
              className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white py-2 rounded-lg font-semibold transition-all duration-200 disabled:cursor-not-allowed flex items-center justify-center space-x-2 text-sm"
            >
              <Printer className="h-4 w-4" />
              <span>Print Current Receipt</span>
            </button>
            <button
              onClick={() => setShowPayment(true)}
              disabled={cart.length === 0}
              className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 disabled:from-gray-400 disabled:to-gray-400 text-white py-2 rounded-lg font-semibold transition-all duration-200 disabled:cursor-not-allowed flex items-center justify-center space-x-2 text-sm"
            >
              <DollarSign className="h-4 w-4" />
              <span>Process Payment</span>
            </button>
            <button
              onClick={() => {
                setCart([]);
                setAppliedCoupon(null);
                setDiscount(0);
                setCustomerInfo({ name: '', phone: '', email: '', table: '' });
                setOrderNote('');
              }}
              className="w-full bg-gray-600 hover:bg-gray-700 text-white py-2 rounded-lg font-semibold transition-colors text-sm"
            >
              Clear Cart
            </button>
          </div>
        </div>
      </div>

      {/* Saved Orders Modal */}
      {showSavedOrders && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-96 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-800">Saved Orders</h3>
              <button
                onClick={() => setShowSavedOrders(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-3">
              {savedOrders.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No saved orders</p>
              ) : (
                savedOrders.map((order) => (
                  <div key={order.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-medium text-gray-800">
                          {order.customer.name || 'Guest'} - Table {order.customer.table || 'N/A'}
                        </p>
                        <p className="text-sm text-gray-600">
                          {new Date(order.timestamp).toLocaleString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-purple-600">${order.total.toFixed(2)}</p>
                        <p className="text-sm text-gray-500">{order.items.length} items</p>
                      </div>
                    </div>
                    {order.note && (
                      <p className="text-sm text-gray-600 mb-2">Note: {order.note}</p>
                    )}
                    <div className="flex space-x-2">
                      <button
                        onClick={() => loadSavedOrder(order)}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg text-sm font-medium transition-colors"
                      >
                        Load Order
                      </button>
                      <button
                        onClick={() => printReceipt(order)}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg text-sm font-medium transition-colors"
                      >
                        Print Receipt
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPayment && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full border border-gray-200">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-800">Process Payment</h3>
              <button
                onClick={() => setShowPayment(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="mb-6">
              <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">Total Amount</span>
                  <span className="text-3xl font-bold text-purple-600">${total.toFixed(2)}</span>
                </div>
                <div className="text-sm text-gray-500">
                  Payment Method: {paymentMethod === 'card' ? 'Credit/Debit Card' : 'M-Pesa'}
                </div>
                {customerInfo.name && (
                  <div className="text-sm text-gray-500 mt-1">
                    Customer: {customerInfo.name} {customerInfo.table && `(Table ${customerInfo.table})`}
                  </div>
                )}
              </div>

              {/* Payment Methods */}
              <div className="mb-4">
                <h4 className="font-semibold mb-3 text-gray-800">Payment Method</h4>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setPaymentMethod('card')}
                    className={`flex items-center justify-center space-x-2 p-3 rounded-lg border transition-all ${
                      paymentMethod === 'card'
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <CreditCard className="h-5 w-5" />
                    <span>Card</span>
                  </button>
                  <button
                    onClick={() => setPaymentMethod('mpesa')}
                    className={`flex items-center justify-center space-x-2 p-3 rounded-lg border transition-all ${
                      paymentMethod === 'mpesa'
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <Smartphone className="h-5 w-5" />
                    <span>M-Pesa</span>
                  </button>
                </div>
              </div>
              
              {paymentMethod === 'card' ? (
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Card Number"
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      placeholder="MM/YY"
                      className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <input
                      type="text"
                      placeholder="CVV"
                      className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <input
                    type="tel"
                    placeholder="M-Pesa Phone Number"
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <p className="text-sm text-gray-600 bg-blue-50 p-3 rounded-lg border border-blue-200">
                    You will receive an M-Pesa prompt on your phone to complete the payment.
                  </p>
                </div>
              )}
            </div>
            
            <button
              onClick={processPayment}
              disabled={isProcessing}
              className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 disabled:from-gray-400 disabled:to-gray-400 text-white py-3 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center space-x-2"
            >
              {isProcessing ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <Check className="h-5 w-5" />
                  <span>Complete Payment</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClubPOS;